import { useCallback, useRef } from 'react';

interface UseMapboxErrorDetectionOptions {
    onFatalError: (reason: string) => void;
    tileErrorThreshold?: number;
    tileErrorWindowMs?: number;
}

interface MapboxErrorEvent {
    error?: {
        status?: number;
        message?: string;
    };
    sourceId?: string;
    type?: string;
}

const DEFAULT_TILE_THRESHOLD = 5;
const DEFAULT_TILE_WINDOW_MS = 3000;

const FATAL_STATUS_CODES = [401, 403, 429];
const FATAL_MESSAGE_PATTERNS = ['failed to fetch', 'networkerror', 'network request failed'];

export const useMapboxErrorDetection = ({
    onFatalError,
    tileErrorThreshold = DEFAULT_TILE_THRESHOLD,
    tileErrorWindowMs = DEFAULT_TILE_WINDOW_MS,
}: UseMapboxErrorDetectionOptions) => {
    const tileErrorTimestampsRef = useRef<number[]>([]);
    const triggeredRef = useRef(false);

    const classifyFatal = (event: MapboxErrorEvent): string | null => {
        const status = event.error?.status;
        const message = (event.error?.message || '').toLowerCase();

        if (status && FATAL_STATUS_CODES.includes(status)) {
            return `mapbox-status-${status}`;
        }

        if (message && FATAL_MESSAGE_PATTERNS.some((p) => message.includes(p))) {
            return 'mapbox-network-error';
        }

        return null;
    };

    const handler = useCallback((event: MapboxErrorEvent) => {
        if (triggeredRef.current) return;

        const fatalReason = classifyFatal(event);

        if (fatalReason) {
            triggeredRef.current = true;
            onFatalError(fatalReason);

            return;
        }

        if (event.sourceId) {
            const now = Date.now();
            const recent = tileErrorTimestampsRef.current.filter((t) => now - t <= tileErrorWindowMs);

            recent.push(now);
            tileErrorTimestampsRef.current = recent;

            if (recent.length >= tileErrorThreshold) {
                triggeredRef.current = true;
                onFatalError('mapbox-tile-error-burst');
            }
        }
    }, [onFatalError, tileErrorThreshold, tileErrorWindowMs]);

    const reset = useCallback(() => {
        tileErrorTimestampsRef.current = [];
        triggeredRef.current = false;
    }, []);

    return { handler, reset };
};
